import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Chip,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Palette as PaletteIcon
} from '@mui/icons-material';
import VariantCard from './VariantCard';

/**
 * Componente para gestionar variantes (colores, tallas, imágenes) al agregar stock
 * @param {Object} props
 * @param {Array} props.variants - Array de variantes actuales
 * @param {Function} props.onChange - Callback cuando cambian las variantes
 */
const VariantsManager = ({ variants = [], onChange, maxQuantity }) => {
  const [typeDetails, setTypeDetails] = useState(() => {
    return (variants || []).map((v, idx) => ({
      ...v,
      _id: v._id || `variant-${Date.now()}-${idx}-${Math.random()}`
    }));
  });

  // Sincronizar cuando el padre provee variantes después del primer render
  // (ej: el artículo cargó con delay y variants llegó vacío inicialmente)
  const lastVariantsRef = useRef(variants);
  useEffect(() => {
    const prev = lastVariantsRef.current;
    if (prev === variants) return;
    // Solo sincronizar si el array realmente cambió en contenido
    // (para no pisar ediciones del usuario con un reload innecesario)
    const prevJson = JSON.stringify(prev.map(({ _id, ...r }) => r));
    const nextJson = JSON.stringify((variants || []).map(({ _id, ...r }) => r));
    if (prevJson !== nextJson) {
      setTypeDetails((variants || []).map((v, idx) => ({
        ...v,
        _id: v._id || `variant-${Date.now()}-${idx}-${Math.random()}`
      })));
    }
    lastVariantsRef.current = variants;
  }, [variants]);

  // Helper para limpiar _id temporal antes de enviar al padre
  const cleanAndNotify = useCallback((newVariants) => {
    const cleaned = newVariants.map(({ _id, ...rest }) => rest);
    // Actualizar la ref ANTES de llamar onChange para que el useEffect
    // de sincronización no vuelva a pisar el estado con los mismos datos
    // (esto evita que el TextField de color pierda el foco en cada tecla)
    lastVariantsRef.current = cleaned;
    onChange(cleaned);
  }, [onChange]);

  const handleAddVariant = useCallback(() => {
    const newVariants = [
      ...typeDetails,
      {
        color: '',
        sizes: [],
        images: [],
        available: true,
        quantity: 0,
        _id: `variant-${Date.now()}-${typeDetails.length}-${Math.random()}`
      }
    ];
    setTypeDetails(newVariants);
    cleanAndNotify(newVariants);
  }, [typeDetails, cleanAndNotify]);

  const handleRemoveVariant = useCallback((index) => {
    const newVariants = typeDetails.filter((_, i) => i !== index);
    setTypeDetails(newVariants);
    cleanAndNotify(newVariants);
  }, [typeDetails, cleanAndNotify]);

  const handleVariantChange = useCallback((index, field, value) => {
    const newVariants = [...typeDetails];
    newVariants[index] = {
      ...newVariants[index],
      [field]: value
    };
    setTypeDetails(newVariants);
    cleanAndNotify(newVariants);
  }, [typeDetails, cleanAndNotify]);

  // Handler atómico para size_details + quantity (evita doble setState con closure stale)
  const handleSizeDetailsChange = useCallback((index, newSizeDetails, totalQty) => {
    const newVariants = [...typeDetails];
    newVariants[index] = {
      ...newVariants[index],
      size_details: newSizeDetails,
      quantity: totalQty,
    };
    setTypeDetails(newVariants);
    cleanAndNotify(newVariants);
  }, [typeDetails, cleanAndNotify]);

  const handleAddSize = useCallback((index, size) => {
    if (!size.trim()) return;
    const newVariants = [...typeDetails];
    const currentSizes = newVariants[index].sizes || [];
    if (!currentSizes.includes(size.trim())) {
      newVariants[index].sizes = [...currentSizes, size.trim()];
      // Agregar al size_details si no existe aún
      const currentSizeDetails = newVariants[index].size_details || [];
      if (!currentSizeDetails.find(sd => sd.size === size.trim())) {
        newVariants[index].size_details = [...currentSizeDetails, { size: size.trim(), quantity: 0, available: false }];
      }
      setTypeDetails(newVariants);
      cleanAndNotify(newVariants);
    }
  }, [typeDetails, cleanAndNotify]);

  const handleRemoveSize = useCallback((variantIndex, size) => {
    const newVariants = [...typeDetails];
    newVariants[variantIndex].sizes = newVariants[variantIndex].sizes.filter(s => s !== size);
    // Sincronizar size_details
    if (newVariants[variantIndex].size_details) {
      newVariants[variantIndex].size_details = newVariants[variantIndex].size_details.filter(sd => sd.size !== size);
      const totalQty = newVariants[variantIndex].size_details.reduce((acc, sd) => acc + (sd.quantity || 0), 0);
      newVariants[variantIndex].quantity = totalQty;
    }
    setTypeDetails(newVariants);
    cleanAndNotify(newVariants);
  }, [typeDetails, cleanAndNotify]);

  const handleAddImage = useCallback((index, imageUrl) => {
    console.log('handleAddImage llamado con index:', index, 'url:', imageUrl);
    if (!imageUrl.trim()) return;
    const newVariants = [...typeDetails];
    console.log('Estado actual typeDetails:', typeDetails);
    const currentImages = newVariants[index].images || [];
    if (!currentImages.includes(imageUrl.trim())) {
      newVariants[index].images = [...currentImages, imageUrl.trim()];
      console.log('Agregando imagen al index', index, 'nuevo array de imágenes:', newVariants[index].images);
      setTypeDetails(newVariants);
      cleanAndNotify(newVariants);
    }
  }, [typeDetails, cleanAndNotify]);

  const handleRemoveImage = useCallback((variantIndex, imageUrl) => {
    const newVariants = [...typeDetails];
    newVariants[variantIndex].images = newVariants[variantIndex].images.filter(img => img !== imageUrl);
    setTypeDetails(newVariants);
    cleanAndNotify(newVariants);
  }, [typeDetails, cleanAndNotify]);

  const totalVariantQty = typeDetails.reduce((sum, v) => sum + (v.quantity || 0), 0);
  const exceedsMax = maxQuantity !== undefined && maxQuantity > 0 && totalVariantQty > maxQuantity;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 2 },
        mt: 2,
        border: `1px solid ${exceedsMax ? '#d32f2f' : 'rgba(103,58,183,0.2)'}`,
        borderRadius: 2,
        backgroundColor: '#fafafa',
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
        mb={1.5}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <PaletteIcon sx={{ color: '#673ab7', fontSize: 22 }} />
          <Typography variant="subtitle1" sx={{ color: '#673ab7', fontWeight: 700 }}>
            Gestionar Variantes
          </Typography>
          {typeDetails.length > 0 && (
            <Chip
              label={typeDetails.length}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 700,
                backgroundColor: '#ede7f6',
                color: '#673ab7',
              }}
            />
          )}
          {maxQuantity !== undefined && maxQuantity > 0 && typeDetails.length > 0 && (
            <Chip
              label={`${totalVariantQty} / ${maxQuantity} u.`}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 700,
                backgroundColor: exceedsMax
                  ? '#ffebee'
                  : totalVariantQty === maxQuantity
                  ? '#e8f5e9'
                  : '#f5f5f5',
                color: exceedsMax
                  ? '#c62828'
                  : totalVariantQty === maxQuantity
                  ? '#2e7d32'
                  : 'text.secondary',
              }}
            />
          )}
        </Box>
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddVariant}
          variant="outlined"
          size="small"
          sx={{
            borderColor: '#673ab7',
            color: '#673ab7',
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': { backgroundColor: '#f8f5ff', borderColor: '#673ab7' },
          }}
        >
          Agregar color
        </Button>
      </Box>

      <Divider sx={{ mb: exceedsMax ? 1 : 1.5 }} />

      {exceedsMax && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1.5, fontWeight: 600 }}>
          ⚠ La suma de variantes ({totalVariantQty} u.) supera la cantidad a agregar ({maxQuantity} u.)
        </Typography>
      )}

      {typeDetails.length === 0 ? (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            backgroundColor: 'rgba(0,0,0,0.02)',
            borderRadius: 2,
            border: '1px dashed rgba(0,0,0,0.12)'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No hay variantes definidas. Haz clic en "Agregar Color" para comenzar.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {typeDetails.map((variant, index) => (
            <VariantCard
              key={variant._id}
              variant={variant}
              index={index}
              onRemove={handleRemoveVariant}
              onChange={handleVariantChange}
              onChangeSizeDetails={handleSizeDetailsChange}
              onAddSize={handleAddSize}
              onRemoveSize={handleRemoveSize}
              onAddImage={handleAddImage}
              onRemoveImage={handleRemoveImage}
            />
          ))}
        </Stack>
      )}
    </Paper>
  );
};

export default VariantsManager;
